import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  CircularProgress,
  Autocomplete,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { usersApi, rolesApi, UserPermission, User as UserType, Role } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { MODULES, MODULE_ALIASES } from '../constants/modules';

/** Build a fresh permissions array covering every known module */
const buildDefaultPermissions = () =>
  MODULES.map(module => ({
    module: module.id,
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
  }));

const initialFormData = {
  name: '',
  email: '',
  password: '',
  role: 'USER',
  role_id: null as number | null,
  permissions: buildDefaultPermissions(),
};

/**
 * Normalize an API-returned permission so the module id uses the
 * canonical hyphenated form the frontend expects.
 */
function normalizeModuleId(moduleId: string): string {
  return MODULE_ALIASES[moduleId] || moduleId;
}

/**
 * Merge API permissions into a complete list covering every known module.
 * API may return underscore IDs or miss newly added modules — this fills the gaps.
 */
function mergePermissions(apiPermissions: UserPermission[]): UserPermission[] {
  const map = new Map<string, UserPermission>();
  for (const p of apiPermissions) {
    map.set(normalizeModuleId(p.module), { ...p, module: normalizeModuleId(p.module) });
  }
  return MODULES.map(m =>
    map.get(m.id) || { module: m.id, can_view: false, can_add: false, can_edit: false, can_delete: false },
  );
}

function getApiErrorMessage(err: any, fallback: string): string {
  return err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || fallback;
}

export default function UserManagement() {
  const { user: authUser, loading: authLoading } = useAuth();
  const companyId = authUser?.company?.id;

  const [users, setUsers] = useState<UserType[]>([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordUserId, setPasswordUserId] = useState<number | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchRoles = useCallback(async () => {
    if (!companyId) return;
    try {
      setRoles(await rolesApi.getAll(companyId));
    } catch {
      // Roles are optional; silently fail
    }
  }, [companyId]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!companyId && authUser?.role !== 'MASTER') {
        setError('No company context available. Please contact support.');
        return;
      }

      if (authUser?.role === 'MASTER') {
        setUsers(await usersApi.getAll());
      } else if (companyId) {
        setUsers(await usersApi.getCompanyUsers(companyId));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [authUser, companyId]);

  useEffect(() => {
    if (!authLoading) {
      fetchUsers();
      fetchRoles();
    }
  }, [authLoading, fetchUsers, fetchRoles]);

  // ── Dialog helpers ─────────────────────────────────────────────────────────
  const handleOpen = () => {
    setFormData(initialFormData);
    setSelectedId(null);
    setError(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Permissions toggle ─────────────────────────────────────────────────────
  const handlePermissionChange = (moduleId: string, permission: keyof UserPermission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(p =>
        p.module === moduleId ? { ...p, [permission]: !p[permission] } : p,
      ),
    }));
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateForm = (): string | null => {
    if (!formData.name.trim()) return 'Name is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email address';
    if (!selectedId && !formData.password) return 'Password is required';
    if (!selectedId && formData.password.length < 6) return 'Password must be at least 6 characters';
    return null;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }
    if (!companyId) { setError('Company context not found. Please log in again.'); return; }

    try {
      setError(null);
      setSaving(true);

      if (selectedId) {
        await usersApi.update(companyId, selectedId, {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          role_id: formData.role_id,
          permissions: formData.permissions,
        });
        setSuccess('User updated successfully!');
      } else {
        await usersApi.create(companyId, {
          ...formData,
          role_id: formData.role_id,
        });
        setSuccess('User created successfully!');
      }

      setOpen(false);
      fetchUsers();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save user'));
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const handleEdit = (u: UserType) => {
    setSelectedId(u.id);
    setFormData({
      name: u.name,
      email: u.email,
      password: '',
      role: u.role,
      role_id: u.role_id || null,
      permissions: mergePermissions(u.permissions),
    });
    setError(null);
    setOpen(true);
  };

  // ── Deactivate / Reactivate ────────────────────────────────────────────────
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to deactivate this user? They will no longer be able to access the system.')) return;
    if (!companyId) { setError('Company context not found.'); return; }
    try {
      setError(null);
      await usersApi.deactivate(companyId, id);
      setSuccess('User deactivated successfully!');
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to deactivate user');
    }
  };

  const handleReactivate = async (id: number) => {
    if (!window.confirm('Are you sure you want to reactivate this user?')) return;
    if (!companyId) { setError('Company context not found.'); return; }
    try {
      setError(null);
      await usersApi.reactivate(companyId, id);
      setSuccess('User reactivated successfully!');
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reactivate user');
    }
  };

  // ── Change Password ────────────────────────────────────────────────────────
  const handleOpenChangePassword = (userId: number) => {
    setPasswordUserId(userId);
    setPasswordData({ newPassword: '', confirmPassword: '' });
    setPasswordError(null);
    setPasswordSuccess(null);
    setChangePasswordOpen(true);
  };

  const handleCloseChangePassword = () => {
    setChangePasswordOpen(false);
    setPasswordUserId(null);
    setPasswordError(null);
    setPasswordSuccess(null);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmitChangePassword = async () => {
    if (!passwordData.newPassword || passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (!companyId) { setPasswordError('Company context not found.'); return; }
    try {
      setPasswordError(null);
      await usersApi.changePassword(companyId, passwordUserId!, { password: passwordData.newPassword });
      setPasswordSuccess('Password updated successfully');
      setTimeout(() => handleCloseChangePassword(), 1500);
    } catch (err: any) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    }
  };

  // ── Loading guard ──────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar open={!!error && !open} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>

      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">{success}</Alert>
      </Snackbar>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">User Management</Typography>
        <Button variant="contained" color="primary" onClick={handleOpen}>
          Add New User
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>System Role</TableCell>
                <TableCell>Assigned Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length > 0 ? (
                users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell>
                      {u.assigned_role ? (
                        <Chip label={u.assigned_role.name} size="small" color="primary" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">Custom</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: u.is_active ? 'success.main' : 'error.main', fontWeight: 'medium' }}
                      >
                        {u.is_active ? 'Active' : 'Inactive'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenChangePassword(u.id)} color="primary" title="Change Password">
                        <VpnKeyIcon />
                      </IconButton>
                      <IconButton onClick={() => handleEdit(u)} color="primary" title="Edit">
                        <EditIcon />
                      </IconButton>
                      {u.is_active ? (
                        <IconButton onClick={() => handleDelete(u.id)} color="error" title="Deactivate">
                          <DeleteIcon />
                        </IconButton>
                      ) : (
                        <IconButton onClick={() => handleReactivate(u.id)} color="success" title="Reactivate">
                          <RestoreIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No users found. Click "Add New User" to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Add / Edit Dialog ──────────────────────────────────────────────── */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{selectedId ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Name" name="name"
                value={formData.name} onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Email" name="email" type="email"
                value={formData.email} onChange={handleInputChange}
                required
              />
            </Grid>
            {!selectedId && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth label="Password" name="password" type="password"
                  value={formData.password} onChange={handleInputChange}
                  required
                  helperText="Minimum 6 characters"
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Role" name="role" select
                SelectProps={{ native: true }}
                value={formData.role} onChange={handleInputChange}
                helperText="MASTER role has full access regardless of permission settings."
              >
                <option value="USER">User</option>
                <option value="MASTER">Master</option>
              </TextField>
            </Grid>
            {/* Assigned Role */}
            <Grid item xs={12}>
              <Autocomplete
                options={roles}
                getOptionLabel={(r) => r.name}
                value={roles.find(r => r.id === formData.role_id) || null}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{option.name}</Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">{option.description}</Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign Role"
                    helperText={formData.role_id
                      ? 'Permissions are managed by this role. Edit the role to change permissions.'
                      : 'Select a role to auto-fill permissions, or leave empty for custom permissions.'}
                  />
                )}
                onChange={(_e, selectedRole) => {
                  if (selectedRole) {
                    setFormData(prev => ({
                      ...prev,
                      role_id: selectedRole.id,
                      permissions: mergePermissions(selectedRole.permissions),
                    }));
                  } else {
                    setFormData(prev => ({ ...prev, role_id: null }));
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Permissions</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {formData.role_id
                  ? 'Permissions are set by the assigned role. Clear the role to customize individually.'
                  : 'Set access permissions for each module. Dashboard is accessible to all users regardless of permissions.'}
              </Typography>
              {!formData.role_id && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 260, flex: 1 }}
                  options={users.filter(u => u.is_active && u.id !== selectedId)}
                  getOptionLabel={(u) => `${u.name} (${u.email})`}
                  renderInput={(params) => (
                    <TextField {...params} label="Copy permissions from..." size="small" />
                  )}
                  onChange={(_e, selectedUser) => {
                    if (selectedUser) {
                      setFormData(prev => ({
                        ...prev,
                        permissions: mergePermissions(selectedUser.permissions),
                      }));
                      setSuccess(`Copied permissions from ${selectedUser.name}`);
                    }
                  }}
                  value={null}
                  blurOnSelect
                />
                <Button
                  size="small" variant="outlined"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    permissions: MODULES.map(m => ({
                      module: m.id, can_view: true, can_add: true, can_edit: true, can_delete: true,
                    })),
                  }))}
                >
                  Select All
                </Button>
                <Button
                  size="small" variant="outlined" color="inherit"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    permissions: buildDefaultPermissions(),
                  }))}
                >
                  Deselect All
                </Button>
              </Box>
              )}
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Module</TableCell>
                      <TableCell align="center">View</TableCell>
                      <TableCell align="center">Add</TableCell>
                      <TableCell align="center">Edit</TableCell>
                      <TableCell align="center">Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {MODULES.map((module) => {
                      const permission = formData.permissions.find(p => p.module === module.id)
                        || { module: module.id, can_view: false, can_add: false, can_edit: false, can_delete: false };

                      return (
                        <TableRow key={module.id}>
                          <TableCell>{module.label}</TableCell>
                          <TableCell align="center">
                            <Checkbox checked={permission.can_view} onChange={() => handlePermissionChange(module.id, 'can_view')} color="primary" size="small" disabled={!!formData.role_id} />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox checked={permission.can_add} onChange={() => handlePermissionChange(module.id, 'can_add')} color="primary" size="small" disabled={!!formData.role_id} />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox checked={permission.can_edit} onChange={() => handlePermissionChange(module.id, 'can_edit')} color="primary" size="small" disabled={!!formData.role_id} />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox checked={permission.can_delete} onChange={() => handlePermissionChange(module.id, 'can_delete')} color="primary" size="small" disabled={!!formData.role_id} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : selectedId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Change Password Dialog ─────────────────────────────────────────── */}
      <Dialog open={changePasswordOpen} onClose={handleCloseChangePassword} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
          {passwordSuccess && <Alert severity="success" sx={{ mb: 2 }}>{passwordSuccess}</Alert>}
          <TextField
            autoFocus margin="dense" name="newPassword" label="New Password"
            type="password" fullWidth
            value={passwordData.newPassword} onChange={handlePasswordChange}
            helperText="Minimum 6 characters"
          />
          <TextField
            margin="dense" name="confirmPassword" label="Confirm Password"
            type="password" fullWidth
            value={passwordData.confirmPassword} onChange={handlePasswordChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangePassword}>Cancel</Button>
          <Button onClick={handleSubmitChangePassword} variant="contained" color="primary">
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
